import {Injectable} from "@angular/core";
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import * as init from "./init.actions";
import {exhaustMap, forkJoin, Observable, of, switchMap, withLatestFrom} from 'rxjs';
import {catchError, filter, map, tap} from "rxjs/operators";
import {Serializer, SerializerResponse, Serializers} from "../../interfaces/serializer";
import {ProfileService} from "../../services/profile.service";
import {SpacesService} from "../../services/spaces.service";
import {SitesService} from "../../services/sites.service";
import {DataSetUsagesService} from "../../services/data-set-usages.service";
import {User} from "../../interfaces/user";
import {AppService} from "../../services/app.service";
import {Router} from "@angular/router";
import {AuthenticationService} from "../../services/authentication.service";
import {
  DeleteSiteSuccessAction,
  LogInAction,
  UpdateCurrentSiteAction
} from "./init";
import {Title} from "@angular/platform-browser";
import {Store} from '@ngrx/store';
import {AppState} from "../store";
import {selectInitState, selectSiteID} from "./init.selectors";
import {loadDataSourceDimensions} from '../dataSourceDimensions/dataSourceDimensions.actions';
import {loadDataSourcesMetrics} from "../dataSourcesMetrics/dataSourcesMetrics.actions";
import {loadChannels} from "../channels/channels.actions";
import {loadSpaces} from "../spaces/spaces.actions";
import {loadDataSourcesAll} from "../dataSourcesAll/dataSourcesAll.actions";
import {loadDataSourcesUsage, loadDataSourcesUsageSuccess} from "../dataSourcesUsage/dataSourcesUsage.actions";
import {loadDataSourcesCustom} from "../dataSourcesCustom/dataSourcesCustom.actions";
import {loadDataSourcesOrganic} from "../dataSourcesOrganic/dataSourcesOrganic.actions";
import {loadDataSourcesAd} from "../dataSourcesAd/dataSourcesAd.actions";
import {loadDataSourcesSite} from "../dataSourcesSite/dataSourcesSite.actions";
import {loadMatchings} from "../matchings/matchings.actions";
import {loadSites} from "../sites/sites.actions";
import {loadUsers} from "../users/users.actions";
import {loadExports} from "../exports/exports.actions";
import {loadExportsDestination} from "../exportsDestination/exportsDestination.actions";
import {initLanguages, loadLanguages, updateLanguagesSuccess} from "../languages/languages.actions";
import {Site} from "../../interfaces/site";
import {loadProfileSuccess} from "../profile/profile.actions";
import {WebsocketService} from "../../services/websocket.service";
import {loadAlerts, silentLoadAlerts} from "../alerts/alerts.actions";
import {loadNotificationsExporters, silentLoadNotificationsExporters} from "../notifications-exporters/notifications-exporters.actions";
import {
  listenAlertsWebsocket,
  listenNotificationsExportersWebsocket,
  updateSiteMatchingsStatus
} from "./init.actions";
import {loadTemplates} from "../templates/templates.actions";
import {loadSavedReports} from "../savedReports/savedReports.actions";
import {loadSavedReportsFolders} from "../savedReportsFolders/savedReportsFolders.actions";
import {selectProfile} from "../profile/profile.selectors";
import {PublicReportsService} from '../../services/public-reports.service';
import {WebsocketEvent} from '../../interfaces/websocket';
import {TypedAction} from '@ngrx/store/src/models';
import {Space} from '../../interfaces/space';
import {loadDataSetMetrics, loadDataSetUsageMetrics, updateDataSetMetrics} from '../dataSetMetrics/dataSetMetrics.actions';
import {loadDataSetMetricsGroups, updateDataSetMetricsGroups} from '../dataSetMetricsGroups/dataSetMetricsGroups.actions';
import {loadGeneralDimensions, updateGeneralDimensions} from '../generalDimensions/generalDimensions.actions';
import {loadDataSetDimensions, loadDataSetUsageDimensions, updateDataSetDimensions} from '../dataSetDimensions/dataSetDimensions.actions';
import {loadDataSetDimensionsGroups, updateDataSetDimensionsGroups} from '../dataSetDimensionsGroups/dataSetDimensionsGroups.actions';
import {loadDataSetConditionalDimensions, updateDataSetConditionalDimensions} from '../dataSetConditionalDimensions/dataSetConditionalDimensions.actions';
import {DataSource} from "../../interfaces/data-source";
import {loadNotificationCenter} from "../notificationCenter/notificationCenter.actions";
import {loadDataSourceProviders} from '../dataSourceProviders/dataSourceProviders.actions';
import {
  selectDataSourcesUsageLoaded,
  selectDataSourceUsageComponentObjects
} from "../dataSourcesUsage/dataSourcesUsage.selectors";
import {DatasetUsageComponentObject} from "../../classes/dataset-usage-component-object";

@Injectable()
export class InitEffects {
  private readonly _init$ = createEffect(() => this._actions$
    .pipe(
      ofType(init.initApp),
      switchMap(() => this._profileS.getProfile().pipe(
        switchMap((profile: SerializerResponse<Serializer<User>>) => this._spacesS.getCurrentSpace().pipe(
          map((space: Serializer<Space>) => [profile, space])
        )),
        switchMap(([profile, space]: [SerializerResponse<Serializer<User>>, Serializer<Space>]) => {
          this._appS.reset();
          this._appS.user = profile.data;
          this._appS.space = space;

          return this._datasetsS.getSites(false).pipe(
            switchMap((sites: Serializers<Site>) => {
              const title = this._appS.getPageTitle(this._appS.dataset);

              this._appS.datasets = sites;
              this._appS.setDatasetByID(this._appS.datasetID);

              if (!this._appS.dataset) {
                this._title.setTitle(title);
              }

              return this._datasetUsages.getDataSetUsages(false).pipe(
                tap((dataSources: SerializerResponse<Serializers<DataSource>>) => {
                  this._appS.dataSetUsages = dataSources.data;
                })
              ).pipe(
                switchMap((dataSources: SerializerResponse<Serializers<DataSource>>) => {
                  if (this._appS.dataset) {
                    return [
                      init.initAppSuccess({
                        user: profile.data,
                        site: this._appS.dataset,
                        sites: sites,
                        space: space
                      }),

                      init.initSiteSuccess({
                        site: this._appS.dataset,
                        title: title
                      }),

                      loadDataSourcesUsageSuccess({
                        dataSources: dataSources.data
                      }),

                      loadProfileSuccess({
                        profile: profile.data
                      }),

                      initLanguages({
                        lang: profile.data.attributes.language
                      }),

                      loadLanguages({
                        profile: profile.data
                      }),

                      loadDataSourceDimensions(),
                      loadGeneralDimensions(),
                      loadDataSetDimensions(),
                      loadDataSetConditionalDimensions(),
                      loadDataSetDimensionsGroups(),

                      loadDataSourcesMetrics(),
                      loadDataSetMetrics(),
                      loadDataSetMetricsGroups(),

                      loadChannels(),
                      loadSpaces(),
                      loadDataSourceProviders(),
                      loadDataSourcesAll(),
                      loadDataSourcesCustom(),
                      loadDataSourcesOrganic(),
                      loadDataSourcesAd(),
                      loadDataSourcesSite(),
                      loadMatchings(),
                      loadSites({ costs: false }),
                      loadAlerts(),
                      loadNotificationsExporters(),
                      loadUsers(),
                      loadNotificationCenter(),
                      loadSavedReports({ parameters: false }),
                      loadSavedReportsFolders(),
                      loadExports(),
                      loadTemplates(),
                      loadExportsDestination(),

                      init.listenDsuWebsocket({
                        site: this._appS.dataset
                      }),
                      init.listenOdsuWebsocket({
                        site: this._appS.dataset
                      }),
                      init.listenExportWebsocket({
                        site: this._appS.dataset
                      }),
                      listenAlertsWebsocket({
                        site: this._appS.dataset
                      }),
                      listenNotificationsExportersWebsocket({
                        site: this._appS.dataset
                      }),
                      init.listenExportDestWebsocket({
                        site: this._appS.dataset
                      }),
                      init.listenDsWebsocket({
                        site: this._appS.dataset
                      }),
                      init.listenMatchingsWebsocket({
                        site: this._appS.dataset
                      }),
                      init.listenNotificationsWebsocket({
                        site: this._appS.dataset
                      })
                    ];
                  }

                  return [
                    init.initAppSuccess({
                      site: null,
                      sites: [],
                      space: space,
                      user: profile.data
                    }),
                    initLanguages({
                      lang: profile.data.attributes.language
                    }),
                    loadLanguages({
                      profile: profile.data
                    })
                  ];
                })
              );
            })
          );
        }),
        catchError((error) => {
          if (this._appS.user) {
            return [
              initLanguages({
                lang: 'en'
              }),
              init.initAppFailure({
                user: this._appS.user,
                sites: this._appS.datasets,
                site: this._appS.dataset,
                space: this._appS.space,
                code: error.status
              }),
              loadProfileSuccess({
                profile: this._appS.user
              }),
              initLanguages({
                lang: this._appS.user.attributes.language
              }),
              loadLanguages({
                profile: this._appS.user
              }),
              loadSites({costs: false}),
              loadUsers(),
            ];
          }

          return [
            initLanguages({
              lang: 'en'
            }),
            init.initAppFailure({
              user: null,
              sites: [],
              site: null,
              space: null,
              code: error.status,
            })
          ];
        })
      ))
    )
  );

  private readonly _initAppFailure$ = createEffect(() => this._actions$.pipe(
    ofType(init.initAppFailure),
    switchMap((action) => {
      return of(init.logInRedirect({ code: action.code }));
    })
  ));

  private readonly _initAppSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(init.initAppSuccess),
    map(() => init.logInRedirect({ code: 200 }))
  ));

  private readonly _logIn$ = createEffect(() => this._actions$
    .pipe(
      ofType(init.logIn),
      exhaustMap((params: LogInAction) => this._authenticationS.login(params.credentials)
        .pipe(
          map(_ => {
            this._router.navigate(['loading']);
            return init.logInSuccess();
          }),
          catchError(() => of(init.logInFailure()))
        )
      )
    )
  );

  private readonly _logInRedirect$ = createEffect(() => this._actions$.pipe(
    ofType(init.logInRedirect),
    map((action) => {
      switch (action.code) {
        case 200:
          this._appS.redirect200();
          break;
        case 401:
          this._appS.redirect401();
          break;
        case 402:
          this._appS.redirect402();
          break;
      }

      return init.logInRedirectSuccess();
    })
  ));

  private readonly _logOut$ = createEffect(() => this._actions$
    .pipe(
      ofType(init.logOut),
      exhaustMap(() => this._authenticationS.logout()
        .pipe(
          switchMap(_ => {
            this._appS.reset();
            this._router.navigate(['/login']);
            return [
              init.closeWebsocket(),
              init.logOutSuccess()
            ];
          }),
          catchError(() => of(init.logOutFailure()))
        )
      )
    )
  );

  private readonly _initPublic: Observable<TypedAction<string> & { space: Serializer<Space> }> & CreateEffectMetadata = createEffect(() => this._actions$.pipe(
    ofType(init.initPublic),
    switchMap((action: TypedAction<string> & { uuid: string, token: string }) => forkJoin([
      this._publicReportsService.getDataSet(action.uuid, action.token),
      this._publicReportsService.getSpace(action.uuid, action.token)
    ])),
    map(([dataset, space]: [SerializerResponse<Serializer<Site>>, SerializerResponse<Serializer<Space>>]): TypedAction<string> & { space: Serializer<Space>, dataset: Serializer<Site> } => init.initPublicSuccess({
      dataset: dataset.data,
      space: space.data
    }))
  ));

  private readonly _updateSite$ = createEffect(() => this._actions$
    .pipe(
      ofType(init.initSite),
      exhaustMap((action: UpdateCurrentSiteAction) => {
        let title: string = this._appS.getPageTitle(action.site);

        this._title.setTitle(title);
        this._appS.dataset = action.site;
        this._appS.updateUrlForNewSite(action.site);

        return [
          init.initSiteSuccess({
            title: title,
            site: action.site
          }),

          loadGeneralDimensions(),
          loadDataSetDimensions(),
          loadDataSetConditionalDimensions(),
          loadDataSetDimensionsGroups(),

          loadDataSetMetrics(),
          loadDataSetMetricsGroups(),

          loadDataSourcesUsage(),
          loadChannels(),
          loadDataSourcesCustom(),
          loadDataSourcesOrganic(),
          loadDataSourcesAd(),
          loadDataSourcesSite(),
          loadMatchings(),
          loadSavedReports({ parameters: false }),
          loadSavedReportsFolders(),
          loadAlerts(),
          loadTemplates(),
          loadNotificationsExporters(),
          loadExports(),
          loadExportsDestination(),

          init.unsubscribeWebsocket(),

          init.listenDsuWebsocket({
            site: action.site
          }),
          init.listenOdsuWebsocket({
            site: action.site
          }),
          init.listenExportWebsocket({
            site: action.site
          }),
          init.listenExportDestWebsocket({
            site: action.site
          }),
          init.listenDsWebsocket({
            site: action.site
          }),
          init.listenAlertsWebsocket({
            site: action.site
          }),
          init.listenNotificationsExportersWebsocket({
            site: action.site
          }),
          init.listenMatchingsWebsocket({
            site: action.site
          })
        ];
      })
    )
  );

  private readonly _deleteSiteSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(init.deleteSiteSuccess),
    map((deleteSiteSuccessAction: DeleteSiteSuccessAction) => init.checkSites({
      site: deleteSiteSuccessAction.site
    }))
  ));

  private readonly _initState$ = this._store.select(selectInitState);

  public checkSites$ = createEffect(() => this._actions$.pipe(
    ofType(init.checkSites),
    withLatestFrom(this._initState$),
    exhaustMap(([action, initState]) => {

      if (!initState.sites.length) {
        this._router.navigate(['/create-data-set']);
      } else if (action.site.id === initState.site.id) {
        return [
          init.initSite({
            site: initState.sites[0]
          }),
          init.checkSitesSuccess()
        ];
      }

      return of(init.checkSitesSuccess());
    })
  ));

  private readonly _updateLangSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(updateLanguagesSuccess),
    exhaustMap((): Array<TypedAction<string> & any> => [
      loadDataSourceDimensions(),
      updateGeneralDimensions(),
      updateDataSetDimensions(),
      updateDataSetConditionalDimensions(),
      updateDataSetDimensionsGroups(),

      loadDataSourcesMetrics(),
      updateDataSetMetrics(),
      updateDataSetMetricsGroups(),

      loadDataSourcesUsage(),
      loadTemplates(),
      loadChannels(),
      loadNotificationCenter(),
      loadDataSourcesCustom(),
      loadDataSourcesOrganic(),
      loadDataSourcesAd(),
      loadDataSourcesSite(),
      loadAlerts(),
      loadNotificationsExporters()
    ])
  ));

  private readonly _profile$ = this._store.select(selectProfile);

  private readonly _dataSourcesWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(init.listenDsuWebsocket),
    switchMap((action) =>
      this._webSocketService.get('DatasetChannel', action.site.id).pipe(
        withLatestFrom(this._profile$),
        filter(([wsData, profile]: [WebsocketEvent, Serializer<User>]) => wsData.message['ignore_user'] != profile.id && (wsData.message['dsu'] === 'fast_reload' || wsData.message['dsu'] === 'reload')),
        switchMap((): Array<TypedAction<string> & any> => [
          loadDataSourceDimensions(),
          updateGeneralDimensions(),
          updateDataSetDimensions(),
          updateDataSetConditionalDimensions(),
          updateDataSetDimensionsGroups(),

          loadDataSourcesMetrics(),
          updateDataSetMetrics(),
          updateDataSetMetricsGroups(),

          loadDataSourcesUsage(),
          loadDataSourcesCustom(),
          loadDataSourcesAd(),
          loadDataSourcesSite(),
          loadSavedReportsFolders(),
          loadAlerts()
        ])
      )
    )
  ));

  private readonly _organicDataSourcesWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(init.listenOdsuWebsocket),
    switchMap((action) => this._webSocketService.get('DatasetChannel', action.site.id).pipe(
      withLatestFrom(this._profile$),
      filter(([wsData, profile]: [WebsocketEvent, Serializer<User>]) => wsData.message['ignore_user'] != profile.id && wsData.message['odsu'] === 'fast_reload' || wsData.message['odsu'] === 'reload'),
      switchMap((): Array<TypedAction<string> & any> => [
        loadDataSourceDimensions(),
        updateGeneralDimensions(),
        updateDataSetDimensions(),
        updateDataSetConditionalDimensions(),
        updateDataSetDimensionsGroups(),

        loadDataSourcesMetrics(),
        updateDataSetMetrics(),
        updateDataSetMetricsGroups(),

        loadDataSourcesOrganic(),
        loadDataSourcesCustom(),
        loadDataSourcesAd(),
        loadDataSourcesSite(),
        loadSavedReportsFolders(),
        loadAlerts()
      ])
    ))
  ));

  private readonly _dataSetUsageWebsocket$: Observable<TypedAction<any>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<any>> => this._actions$.pipe(
    ofType(listenAlertsWebsocket),
    switchMap((action: TypedAction<string> & { site: Serializer<Site> }): Observable<TypedAction<any>> => this._webSocketService.get('DatasetChannel', action.site.id).pipe(
      filter((wsData: WebsocketEvent): boolean => 'action' in wsData.message && wsData.message['action'] === 'reload_usage'),
      switchMap((wsData: WebsocketEvent) => this._store.select(selectDataSourcesUsageLoaded).pipe(
        filter((loaded: boolean): boolean => loaded),
        withLatestFrom(this._store.select(selectDataSourceUsageComponentObjects)),
        map(([loaded, usages]: [boolean, DatasetUsageComponentObject[]]): DatasetUsageComponentObject => usages.find((usage: DatasetUsageComponentObject): boolean => parseInt(usage.payload.id as any) === wsData.message['id'])),
        filter((dataSetUsage: DatasetUsageComponentObject): boolean => !!dataSetUsage),
        switchMap((dataSetUsage: DatasetUsageComponentObject): TypedAction<string>[] => [
          loadDataSetUsageDimensions({ dataSetUsage: dataSetUsage }),
          loadDataSetUsageMetrics({ dataSetUsage: dataSetUsage })
        ])
      ))
    ))
  ));

  private readonly _exportsWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(init.listenExportWebsocket),
    switchMap((action) => this._webSocketService.get('DatasetChannel', action.site.id).pipe(
      withLatestFrom(this._profile$),
      filter(([wsData, profile]: [WebsocketEvent, Serializer<User>]) => wsData.message['ignore_user'] != profile.id && wsData.message['export'] === 'fast_reload' || wsData.message['export'] === 'reload'),
      switchMap((): Array<TypedAction<string> & any> => [
        loadExports()
      ])
    ))
  ));

  private readonly _alertsWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(listenAlertsWebsocket),
    switchMap((action) => this._webSocketService.get('DatasetChannel', action.site.id, this._appS.user).pipe(
      withLatestFrom(this._profile$),
      filter(([wsData, profile]: [WebsocketEvent, Serializer<User>]) => wsData.message['ignore_user'] != profile.id && wsData.message['notification'] === 'fast_reload'),
      switchMap((): Array<TypedAction<string> & any> => [
        silentLoadAlerts()
      ])
    ))
  ));

  private readonly _notificationsExportersWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(listenNotificationsExportersWebsocket),
    switchMap((action) => this._webSocketService.get('DatasetChannel', action.site.id).pipe(
      withLatestFrom(this._profile$),
      filter(([wsData, profile]: [WebsocketEvent, Serializer<User>]) => wsData.message['ignore_user'] != profile.id && wsData.message['notification-dest'] === 'fast_reload'),
      switchMap((): Array<TypedAction<string> & any> => [
        silentLoadNotificationsExporters()
      ])
    ))
  ));

  private readonly _exportDestinationsWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(init.listenExportDestWebsocket),
    switchMap((action) => this._webSocketService.get('DatasetChannel', action.site.id).pipe(
      withLatestFrom(this._profile$),
      filter(([wsData, profile]: [WebsocketEvent, Serializer<User>]) => wsData.message['ignore_user'] != profile.id && wsData.message['export-dest'] === 'fast_reload' || wsData.message['export-dest'] === 'reload'),
      switchMap((): Array<TypedAction<string> & any> => [
        loadExportsDestination()
      ])
    ))
  ));

  private readonly _datasetsWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(init.listenDsWebsocket),
    switchMap((action) => this._webSocketService.get('NotificationChannel', action.site.id).pipe(
      withLatestFrom(this._profile$),
      filter(([wsData, profile]: [WebsocketEvent, Serializer<User>]) => wsData.message['ignore_user'] != profile.id && wsData.message['ds'] === 'fast_reload' || wsData.message['ds'] === 'reload'),
      switchMap((): Array<TypedAction<string> & any> => [
        loadSites({costs: false})
      ])
    ))
  ));

  private readonly _matchingsWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(init.listenMatchingsWebsocket),
    switchMap((action) => this._webSocketService.get('DatasetChannel', action.site.id).pipe(
      filter((wsData: WebsocketEvent): boolean => 'data-set-status' in wsData.message),
      switchMap((wsData: WebsocketEvent): Array<TypedAction<string> & any> => [
        updateSiteMatchingsStatus({
          status: wsData.message['data-set-status'].status
        })
      ])
    ))
  ));

  private readonly _notificationsWebsocket$: Observable<any> & CreateEffectMetadata = createEffect(() => this._actions$.pipe(
    ofType(init.listenNotificationsWebsocket),
    switchMap(() => new WebsocketService().get('NotificationChannel', 'all').pipe(
      map((): TypedAction<string> => loadNotificationCenter())
    ))
  ));

  private readonly _unsubscribeWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(init.unsubscribeWebsocket),
    map(() => {
      this._webSocketService.unsubscribe();
      return init.unsubscribeWebsocketSuccess();
    })
  ));

  private readonly _closeWebsocket$ = createEffect(() => this._actions$.pipe(
    ofType(init.closeWebsocket),
    map(() => {
      this._webSocketService.close();
      return init.closeWebsocketSuccess();
    })
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _appS: AppService,
    private readonly _actions$: Actions,
    private readonly _profileS: ProfileService,
    private readonly _spacesS: SpacesService,
    private readonly _datasetsS: SitesService,
    private readonly _datasetUsages: DataSetUsagesService,
    private readonly _router: Router,
    private readonly _authenticationS: AuthenticationService,
    private readonly _title: Title,
    private readonly _webSocketService: WebsocketService,
    private readonly _publicReportsService: PublicReportsService
  ) {}

}
